
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "exporting-candidate-profiles"
    }}>{`Exporting candidate profiles`}</h1>
    <p>{`The SEEK API lets you retrieve information about a candidate and their profile related to a particular application.`}</p>
    <p>{`When a candidate applies for a job ad they consent to a snapshot of their SEEK Profile being shared with the hirer.
SEEK recommends that any retrieved candidate information only be available for viewing under the job ad that the application was related to.
If you decide to persist a general candidate profile in your software, it must be scoped to the SEEK hirer that received the application.`}</p>
    <h2 {...{
      "id": "before-you-begin"
    }}>{`Before you begin`}</h2>
    <p>{`Before you start working with candidate profiles, you will need to `}<a parentName="p" {...{
        "href": "/auth/partner-tokens"
      }}>{`request a partner token`}</a>{`.`}</p>
    <h2 {...{
      "id": "data-structure"
    }}>{`Data structure`}</h2>
    <img alt="" data-scoobie-style="none" height="427" src={require('../../../../mermaid/.8526717c68e2b6599d4493a90888c1cc4fd75612.mmd.svg')} title="Candidate application profile objects" width="420" />
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/CandidateProfile"
      }}><inlineCode parentName="a">{`CandidateProfile`}</inlineCode></a>{` object links to:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`The candidate that the profile belongs to via the `}<a parentName="p" {...{
            "href": "https://developer.seek.com/schema/#/named-type/CandidateProfile/field/candidate"
          }}><inlineCode parentName="a">{`CandidateProfile.candidate`}</inlineCode></a>{` field.`}</p>
        <ul parentName="li">
          <li parentName="ul">
            <p parentName="li"><a parentName="p" {...{
                "href": "https://developer.seek.com/schema/#/named-type/Candidate/field/person"
              }}><inlineCode parentName="a">{`Candidate.person`}</inlineCode></a>{` contains the candidate’s name, home location, email address, and phone number.`}</p>
            <p parentName="li">{`This field is connected to the candidate’s live SEEK Profile;
the query response will reflect updates made by the candidate after applying.`}</p>
          </li>
          <li parentName="ul">
            <p parentName="li"><a parentName="p" {...{
                "href": "https://developer.seek.com/schema/#/named-type/Candidate/field/documentId"
              }}><inlineCode parentName="a">{`Candidate.documentId`}</inlineCode></a>{` can be used as a stable identifier for the candidate across multiple applications.`}</p>
          </li>
        </ul>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The `}<a parentName="p" {...{
            "href": "/use-cases/job-posting/managing-job-ads"
          }}>{`job ad`}</a>{` that the candidate is applying to via the `}<a parentName="p" {...{
            "href": "https://developer.seek.com/schema/#/named-type/CandidateProfile/field/associatedPositionProfile"
          }}><inlineCode parentName="a">{`CandidateProfile.associatedPositionProfile`}</inlineCode></a>{` field.`}</p>
        <p parentName="li">{`You can use this to associate the candidate application with a SEEK job ad and hirer.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The `}<a parentName="p" {...{
            "href": "/use-cases/job-posting/position-openings"
          }}>{`position opening`}</a>{` that the candidate is applying to via the `}<a parentName="p" {...{
            "href": "https://developer.seek.com/schema/#/named-type/CandidateProfile/field/associatedPositionOpenings"
          }}><inlineCode parentName="a">{`CandidateProfile.associatedPositionOpenings`}</inlineCode></a>{` field.`}</p>
        <p parentName="li">{`You’ll likely be better off linking through the position profile;
this field is supported to align with HR-JSON.`}</p>
      </li>
    </ul>
    <p>{`The candidate profile itself contains:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Structured details of the candidate’s employment history, education, and more.`}</p>
        <p parentName="li">{`These fields are taken as a snapshot of the candidate’s SEEK Profile when they apply through the SEEK website or app.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Links to `}<a parentName="p" {...{
            "href": "/use-cases/application-export/attachments"
          }}>{`attachments`}</a>{` such as the candidate’s resume and cover letter.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The `}<a parentName="p" {...{
            "href": "/use-cases/application-export/questionnaire-submissions"
          }}>{`candidate’s responses`}</a>{` to the job’s screening questions via a `}<inlineCode parentName="p">{`seekQuestionnaireSubmission`}</inlineCode>{` field.
This will only be present if a `}<a parentName="p" {...{
            "href": "/use-cases/job-posting/questionnaires"
          }}>{`questionnaire`}</a>{` was provided when posting the job ad.`}</p>
      </li>
    </ul>
    <p>{`For detailed information on all available fields, refer to the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/CandidateProfile"
      }}><inlineCode parentName="a">{`CandidateProfile`}</inlineCode>{` schema definition`}</a>{`.
Only a name and email address are required to create a SEEK candidate account;
all other fields in a candidate’s profile are optional.`}</p>
    <p>{`SEEK may redact fields and omit `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Personal_data"
      }}>{`PII`}</a>{` when a candidate or application is deleted.
This includes attachments and questionnaire submissions.`}</p>
    <p>{`The SEEK API does not translate user input and will serve SEEK Profile information as supplied by the candidate.
As a result, your software may need to handle multilingual candidate data.`}</p>
    <h2 {...{
      "id": "operations"
    }}>{`Operations`}</h2>
    <p>{`Use the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/candidateProfile"
      }}>{`GraphQL `}<inlineCode parentName="a">{`candidateProfile`}</inlineCode>{` query`}</a>{` to retrieve candidate profiles.
You should combine this with the query you use to retrieve `}<a parentName="p" {...{
        "href": "/use-cases/application-export/attachments"
      }}>{`attachments`}</a>{` and `}<a parentName="p" {...{
        "href": "/use-cases/application-export/questionnaire-submissions"
      }}>{`questionnaire submissions`}</a>{`.`}</p>
    <h3 {...{
      "id": "candidateprofile"
    }}>{`candidateProfile`}</h3>
    <pre><code parentName="pre" {...{
        "5550": true,
        "5723": true,
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($id: String!) {\\n  candidateProfile(id: $id) {\\n    profileId {\\n      value\\n    }\\n    createDateTime\\n    candidate {\\n      person {\\n        name {\\n          given\\n          family\\n        }\\n        communication {\\n          address {\\n            # Select other address fields as necessary\\n            formattedAddress\\n            countryCode\\n          }\\n          phone {\\n            formattedNumber\\n          }\\n          email {\\n            address\\n          }\\n        }\\n      }\\n    }\\n    associatedPositionProfile {\\n      seekHirerJobReference\\n      positionOrganizations {\\n        id {\\n          value\\n        }\\n        name\\n      }\\n      positionUri\\n    }\\n    employment {\\n      organization {\\n        name\\n      }\\n      positionHistories {\\n        start\\n        end\\n        title\\n      }\\n    }\\n    education {\\n      institution {\\n        name\\n      }\\n      educationDegrees {\\n        name\\n        degreeGrantedStatus\\n        date\\n      }\\n      descriptions\\n    }\\n    certifications {\\n      name\\n      issued\\n      effectiveTimePeriod {\\n        validTo\\n      }\\n      issuingAuthority {\\n        name\\n      }\\n      descriptions\\n    }\\n    qualifications {\\n      competencyName\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":74,\"column\":1,\"offset\":3437},\"end\":{\"line\":149,\"column\":4,\"offset\":4646},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"id\\\": \\\"seekAnzPublicTest:candidateProfile:apply:5G5NaFDmy7UigvGy6aT6VT\\\"\\n}\",\"position\":{\"start\":{\"line\":151,\"column\":1,\"offset\":4648},\"end\":{\"line\":155,\"column\":4,\"offset\":4737},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":null,\"value\":\"{\\n  \\\"candidateProfile\\\": {\\n    \\\"profileId\\\": {\\n      \\\"value\\\": \\\"seekAnzPublicTest:candidateProfile:apply:5G5NaFDmy7UigvGy6aT6VT\\\"\\n    },\\n    \\\"createDateTime\\\": \\\"2019-09-03T13:50:23.223Z\\\",\\n    \\\"candidate\\\": {\\n      \\\"person\\\": {\\n        \\\"name\\\": {\\n          \\\"given\\\": \\\"Ella\\\",\\n          \\\"family\\\": \\\"Greenholt\\\"\\n        },\\n        \\\"communication\\\": {\\n          \\\"address\\\": [\\n            {\\n              \\\"formattedAddress\\\": \\\"Coober Pedy SA 5723 AU\\\",\\n              \\\"countryCode\\\": \\\"AU\\\"\\n            }\\n          ],\\n          \\\"phone\\\": [\\n            {\\n              \\\"formattedNumber\\\": \\\"(02) 5550 5678\\\"\\n            }\\n          ],\\n          \\\"email\\\": [\\n            {\\n              \\\"address\\\": \\\"EllaG@gmail.com\\\"\\n            }\\n          ]\\n        }\\n      }\\n    },\\n    \\\"associatedPositionProfile\\\": {\\n      \\\"seekHirerJobReference\\\": \\\"12b5d6bf-4723-4ee9-bdc0-e530ccddd7b7\\\",\\n      // The hirer associated with the job ad\\n      \\\"positionOrganizations\\\": [\\n        {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n          },\\n          \\\"name\\\": \\\"Acme Corp\\\"\\n        }\\n      ],\\n      \\\"positionUri\\\": \\\"https://example.seek.co.nz/job/30000001\\\"\\n    },\\n    // `Career history` from the candidate’s SEEK Profile\\n    \\\"employment\\\": [\\n      {\\n        \\\"organization\\\": {\\n          \\\"name\\\": \\\"Swift Inc\\\"\\n        },\\n        \\\"positionHistories\\\": [\\n          {\\n            \\\"start\\\": \\\"2015-05\\\",\\n            \\\"end\\\": \\\"2018-03\\\",\\n            \\\"title\\\": \\\"Customer Service Representative\\\"\\n          }\\n        ]\\n      },\\n      {\\n        \\\"organization\\\": {\\n          \\\"name\\\": \\\"Ledner Inc\\\"\\n        },\\n        \\\"positionHistories\\\": [\\n          {\\n            \\\"start\\\": \\\"2018-10\\\",\\n            \\\"end\\\": null,\\n            \\\"title\\\": \\\"Customer Service Manager\\\"\\n          }\\n        ]\\n      }\\n    ],\\n    // `Education` from the candidate’s SEEK Profile\\n    \\\"education\\\": [\\n      {\\n        \\\"institution\\\": {\\n          \\\"name\\\": \\\"Adelaide Business School\\\"\\n        },\\n        \\\"educationDegrees\\\": [\\n          {\\n            \\\"name\\\": \\\"Bachelor of Business Administration\\\",\\n            \\\"degreeGrantedStatus\\\": \\\"Granted\\\",\\n            \\\"date\\\": \\\"2016-10\\\"\\n          }\\n        ],\\n        \\\"descriptions\\\": []\\n      },\\n      {\\n        \\\"institution\\\": {\\n          \\\"name\\\": \\\"University of New South Wales\\\"\\n        },\\n        \\\"educationDegrees\\\": [\\n          {\\n            \\\"name\\\": \\\"Masters of Business Administration\\\",\\n            \\\"degreeGrantedStatus\\\": \\\"Granted\\\",\\n            \\\"date\\\": \\\"2018-07\\\"\\n          }\\n        ],\\n        \\\"descriptions\\\": [\\\"3.8 GPA\\\"]\\n      }\\n    ],\\n    // `Licences & certifications` from the candidate’s SEEK Profile\\n    \\\"certifications\\\": [\\n      {\\n        \\\"name\\\": \\\"SA Drivers Licence\\\",\\n        \\\"issued\\\": null,\\n        \\\"effectiveTimePeriod\\\": {\\n          \\\"validTo\\\": \\\"2021-07\\\"\\n        },\\n        \\\"issuingAuthority\\\": null,\\n        \\\"descriptions\\\": []\\n      },\\n      {\\n        \\\"name\\\": \\\"Criminal History Check\\\",\\n        \\\"issued\\\": \\\"2021-01\\\",\\n        \\\"effectiveTimePeriod\\\": {\\n          \\\"validTo\\\": \\\"2023-01\\\"\\n        },\\n        \\\"issuingAuthority\\\": null,\\n        \\\"descriptions\\\": [\\\"Police check passed with no criminal history.\\\"]\\n      },\\n      {\\n        \\\"name\\\": \\\"Working with Children Check\\\",\\n        \\\"issued\\\": null,\\n        \\\"effectiveTimePeriod\\\": null,\\n        \\\"issuingAuthority\\\": {\\n          \\\"name\\\": \\\"Government of South Australia\\\"\\n        },\\n        \\\"descriptions\\\": []\\n      }\\n    ],\\n    // `Skills` from the candidate’s SEEK Profile\\n    \\\"qualifications\\\": [\\n      {\\n        \\\"competencyName\\\": \\\"Team building\\\"\\n      },\\n      {\\n        \\\"competencyName\\\": \\\"Customer focus\\\"\\n      },\\n      {\\n        \\\"competencyName\\\": \\\"Stakeholder management\\\"\\n      }\\n    ]\\n  }\\n}\",\"position\":{\"start\":{\"line\":157,\"column\":1,\"offset\":4739},\"end\":{\"line\":303,\"column\":4,\"offset\":8344},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($id:": true,
        "String!)": true,
        "{\\n": true,
        "": true,
        "candidateProfile(id:": true,
        "$id)": true,
        "profileId": true,
        "value\\n": true,
        "}\\n": true,
        "createDateTime\\n": true,
        "candidate": true,
        "person": true,
        "name": true,
        "given\\n": true,
        "family\\n": true,
        "communication": true,
        "address": true,
        "#": true,
        "Select": true,
        "other": true,
        "fields": true,
        "as": true,
        "necessary\\n": true,
        "formattedAddress\\n": true,
        "countryCode\\n": true,
        "phone": true,
        "formattedNumber\\n": true,
        "email": true,
        "address\\n": true,
        "associatedPositionProfile": true,
        "seekHirerJobReference\\n": true,
        "positionOrganizations": true,
        "id": true,
        "name\\n": true,
        "positionUri\\n": true,
        "employment": true,
        "organization": true,
        "positionHistories": true,
        "start\\n": true,
        "end\\n": true,
        "title\\n": true,
        "education": true,
        "institution": true,
        "educationDegrees": true,
        "degreeGrantedStatus\\n": true,
        "date\\n": true,
        "descriptions\\n": true,
        "certifications": true,
        "issued\\n": true,
        "effectiveTimePeriod": true,
        "validTo\\n": true,
        "issuingAuthority": true,
        "qualifications": true,
        "competencyName\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":74,\"column\":1,\"offset\":3437},\"end\":{\"line\":149,\"column\":4,\"offset\":4646},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"id\\\":": true,
        "\\\"seekAnzPublicTest:candidateProfile:apply:5G5NaFDmy7UigvGy6aT6VT\\\"\\n}\",\"position\":{\"start\":{\"line\":151,\"column\":1,\"offset\":4648},\"end\":{\"line\":155,\"column\":4,\"offset\":4737},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"candidateProfile\\\":": true,
        "\\\"profileId\\\":": true,
        "\\\"value\\\":": true,
        "\\\"seekAnzPublicTest:candidateProfile:apply:5G5NaFDmy7UigvGy6aT6VT\\\"\\n": true,
        "},\\n": true,
        "\\\"createDateTime\\\":": true,
        "\\\"2019-09-03T13:50:23.223Z\\\",\\n": true,
        "\\\"candidate\\\":": true,
        "\\\"person\\\":": true,
        "\\\"name\\\":": true,
        "\\\"given\\\":": true,
        "\\\"Ella\\\",\\n": true,
        "\\\"family\\\":": true,
        "\\\"Greenholt\\\"\\n": true,
        "\\\"communication\\\":": true,
        "\\\"address\\\":": true,
        "[\\n": true,
        "\\\"formattedAddress\\\":": true,
        "\\\"Coober": true,
        "Pedy": true,
        "SA": true,
        "AU\\\",\\n": true,
        "\\\"countryCode\\\":": true,
        "\\\"AU\\\"\\n": true,
        "],\\n": true,
        "\\\"phone\\\":": true,
        "\\\"formattedNumber\\\":": true,
        "\\\"(02)": true,
        "5678\\\"\\n": true,
        "\\\"email\\\":": true,
        "\\\"EllaG@gmail.com\\\"\\n": true,
        "]\\n": true,
        "\\\"associatedPositionProfile\\\":": true,
        "\\\"seekHirerJobReference\\\":": true,
        "\\\"12b5d6bf-4723-4ee9-bdc0-e530ccddd7b7\\\",\\n": true,
        "//": true,
        "The": true,
        "hirer": true,
        "associated": true,
        "with": true,
        "the": true,
        "job": true,
        "ad\\n": true,
        "\\\"positionOrganizations\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n": true,
        "\\\"Acme": true,
        "Corp\\\"\\n": true,
        "\\\"positionUri\\\":": true,
        "\\\"https://example.seek.co.nz/job/30000001\\\"\\n": true,
        "`Career": true,
        "history`": true,
        "from": true,
        "candidate’s": true,
        "SEEK": true,
        "Profile\\n": true,
        "\\\"employment\\\":": true,
        "\\\"organization\\\":": true,
        "\\\"Swift": true,
        "Inc\\\"\\n": true,
        "\\\"positionHistories\\\":": true,
        "\\\"start\\\":": true,
        "\\\"2015-05\\\",\\n": true,
        "\\\"end\\\":": true,
        "\\\"2018-03\\\",\\n": true,
        "\\\"title\\\":": true,
        "\\\"Customer": true,
        "Service": true,
        "Representative\\\"\\n": true,
        "\\\"Ledner": true,
        "\\\"2018-10\\\",\\n": true,
        "null,\\n": true,
        "Manager\\\"\\n": true,
        "`Education`": true,
        "\\\"education\\\":": true,
        "\\\"institution\\\":": true,
        "\\\"Adelaide": true,
        "Business": true,
        "School\\\"\\n": true,
        "\\\"educationDegrees\\\":": true,
        "\\\"Bachelor": true,
        "of": true,
        "Administration\\\",\\n": true,
        "\\\"degreeGrantedStatus\\\":": true,
        "\\\"Granted\\\",\\n": true,
        "\\\"date\\\":": true,
        "\\\"2016-10\\\"\\n": true,
        "\\\"descriptions\\\":": true,
        "[]\\n": true,
        "\\\"University": true,
        "New": true,
        "South": true,
        "Wales\\\"\\n": true,
        "\\\"Masters": true,
        "\\\"2018-07\\\"\\n": true,
        "[\\\"3.8": true,
        "GPA\\\"]\\n": true,
        "`Licences": true,
        "&": true,
        "certifications`": true,
        "\\\"certifications\\\":": true,
        "\\\"SA": true,
        "Drivers": true,
        "Licence\\\",\\n": true,
        "\\\"issued\\\":": true,
        "\\\"effectiveTimePeriod\\\":": true,
        "\\\"validTo\\\":": true,
        "\\\"2021-07\\\"\\n": true,
        "\\\"issuingAuthority\\\":": true,
        "\\\"Criminal": true,
        "History": true,
        "Check\\\",\\n": true,
        "\\\"2021-01\\\",\\n": true,
        "\\\"2023-01\\\"\\n": true,
        "[\\\"Police": true,
        "check": true,
        "passed": true,
        "no": true,
        "criminal": true,
        "history.\\\"]\\n": true,
        "\\\"Working": true,
        "Children": true,
        "\\\"Government": true,
        "Australia\\\"\\n": true,
        "`Skills`": true,
        "\\\"qualifications\\\":": true,
        "\\\"competencyName\\\":": true,
        "\\\"Team": true,
        "building\\\"\\n": true,
        "focus\\\"\\n": true,
        "\\\"Stakeholder": true,
        "management\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":157,\"column\":1,\"offset\":4739},\"end\":{\"line\":303,\"column\":4,\"offset\":8344},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($id: String!) {
  candidateProfile(id: $id) {
    profileId {
      value
    }
    createDateTime
    candidate {
      person {
        name {
          given
          family
        }
        communication {
          address {
            # Select other address fields as necessary
            formattedAddress
            countryCode
          }
          phone {
            formattedNumber
          }
          email {
            address
          }
        }
      }
    }
    associatedPositionProfile {
      seekHirerJobReference
      positionOrganizations {
        id {
          value
        }
        name
      }
      positionUri
    }
    employment {
      organization {
        name
      }
      positionHistories {
        start
        end
        title
      }
    }
    education {
      institution {
        name
      }
      educationDegrees {
        name
        degreeGrantedStatus
        date
      }
      descriptions
    }
    certifications {
      name
      issued
      effectiveTimePeriod {
        validTo
      }
      issuingAuthority {
        name
      }
      descriptions
    }
    qualifications {
      competencyName
    }
  }
}
`}</code></pre>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;